// Global board: best run per pilot from Supabase, per craft (distance) or across craft (efficiency index),
// for today's route or all time. Read-only. Nothing here blocks the game, and every state is drawn:
// loading, empty, error with retry, offline, unconfigured.
import type { AppContext, ScreenHandle } from './app';
import type { BoardRow } from '../types';
import { CRAFT, craftById } from '../data/craft';
import { todaySeed } from '../sim/route';
import { fetchBoard, fetchEfficiencyBoard } from '../net/board';
import { currentUserId } from '../net/identity';
import { isConfigured } from '../net/supabase';
import { h, clear, button } from './dom';
import { fmtNm, fmtIndex } from './format';

type Tab = 'distance' | 'efficiency';
type Period = 'today' | 'all';

const OFFLINE_TEXT = 'You are offline; the board needs a connection.';
const offline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine === false;

export function mountLeaderboard(root: HTMLElement, ctx: AppContext): ScreenHandle {
  let craftId = CRAFT[0]?.id ?? 'trainer-172';
  let tab: Tab = 'distance';
  let period: Period = 'today';
  /** Bumped on every load and on hide, so a late response never paints over a newer one. */
  let gen = 0;

  const title = h('h1', { id: 'leaderboard-title' }, 'Global board');
  const sub = h('p', {}, 'Best run per pilot. Today is this day\'s route; All time is every route.');

  const select = h('select', { id: 'board-craft' });
  for (const c of CRAFT) select.append(h('option', { value: c.id }, `${c.className} · ${c.modelledOn}`));
  select.addEventListener('change', () => { craftId = select.value; void load(); });
  const craftField = h('div.field', {}, h('label', { for: 'board-craft' }, 'Craft'), select);

  const periodSeg = h('div.seg', { role: 'group', 'aria-label': 'Period' });
  const periodButtons = new Map<Period, HTMLButtonElement>();
  for (const [value, label] of [['today', 'Today'], ['all', 'All time']] as const) {
    const b = button(label, () => { period = value; void load(); }, { cls: 'btn', ariaLabel: `Period: ${label}` });
    periodButtons.set(value, b);
    periodSeg.append(b);
  }

  const tabs = h('div.tabs', { role: 'tablist', 'aria-label': 'Board' });
  const tabButtons = new Map<Tab, HTMLButtonElement>();
  for (const [value, label] of [['distance', 'Distance'], ['efficiency', 'Efficiency index']] as const) {
    const b = h('button', { class: 'tab', type: 'button', role: 'tab', id: `board-tab-${value}` }, label);
    b.addEventListener('click', () => { tab = value; void load(); });
    tabButtons.set(value, b);
    tabs.append(b);
  }

  const body = h('div.panel', { role: 'tabpanel' });
  root.append(
    h('div.wrap', {},
      h('header.topbar', {},
        button('← Hangar', () => ctx.go('hangar'), { cls: 'btn btn-quiet', ariaLabel: 'Back to the hangar' }),
        h('div.title', {}, title, sub),
      ),
      tabs,
      h('div.board-controls', {}, craftField, h('div.field', {}, h('span.label', {}, 'Period'), periodSeg)),
      body,
    ),
  );

  function paintControls(): void {
    for (const [value, b] of tabButtons) {
      const selected = value === tab;
      b.setAttribute('aria-selected', String(selected));
      b.tabIndex = selected ? 0 : -1;
    }
    body.setAttribute('aria-labelledby', `board-tab-${tab}`);
    for (const [value, b] of periodButtons) b.setAttribute('aria-pressed', String(value === period));
    craftField.hidden = tab === 'efficiency';
    select.value = craftId;
  }

  function state(text: string, retry = false): void {
    clear(body);
    body.append(h('p.state', { role: 'status' }, text));
    if (retry) body.append(h('div.actions', { style: 'justify-content:center' }, button('Retry', () => void load(), { cls: 'btn' })));
  }

  function skeleton(): void {
    clear(body);
    const sk = h('div.skeleton', { 'aria-hidden': 'true' });
    for (let i = 0; i < 6; i++) sk.append(h('span'));
    body.append(h('p.sr-only', { role: 'status' }, 'Loading the board…'), sk);
  }

  function table(rows: BoardRow[], myId: string | null): void {
    clear(body);
    const eff = tab === 'efficiency';
    const head = h('tr', {},
      h('th', { scope: 'col' }, '#'),
      h('th.pilot', { scope: 'col' }, 'Pilot'),
      h('th', { scope: 'col' }, eff ? 'Index' : 'Distance'),
      h('th', { scope: 'col' }, eff ? 'Craft' : 'Mods'),
      h('th', { scope: 'col' }, 'Date'),
    );
    const tbody = h('tbody');
    rows.forEach((row, i) => {
      const me = myId !== null && row.userId === myId;
      // Nicknames are player text: they go in as text nodes, never as markup.
      const pilot = h('td.pilot', {}, row.nickname);
      if (me) pilot.append(h('span.badge.badge-best.you', {}, 'You'));
      tbody.append(h('tr', { class: me ? 'me' : undefined },
        h('td', {}, String(i + 1)),
        pilot,
        h('td', {}, eff ? fmtIndex(row.efficiencyIndex) : fmtNm(row.distanceNm)),
        h('td', {}, eff ? craftById(row.craftId)?.className ?? row.craftId : String(row.modCount)),
        h('td', {}, row.createdAt.slice(0, 10)),
      ));
    });
    body.append(h('div.table-wrap', {}, h('table.table.board', {}, h('thead', {}, head), tbody)));
  }

  async function load(): Promise<void> {
    const g = ++gen;
    paintControls();
    if (!isConfigured) { state('The global board is not available in this build.'); return; }
    if (offline()) { state(OFFLINE_TEXT, true); return; }
    skeleton();
    const seed = period === 'today' ? todaySeed() : null;
    const [res, myId] = await Promise.all([
      tab === 'distance' ? fetchBoard(craftId, seed) : fetchEfficiencyBoard(seed),
      currentUserId(),
    ]);
    if (g !== gen) return;
    if (!res.ok) {
      state(res.reason === 'offline' ? OFFLINE_TEXT : 'The board could not be loaded.', true);
      return;
    }
    if (!res.rows.length) { state('No runs on this board yet — be the first.'); return; }
    table(res.rows, myId);
  }

  window.addEventListener('online', () => { if (!root.hidden) void load(); });

  function show(): void {
    const id = ctx.session.craftId;
    if (id && craftById(id)) craftId = id;
    void load();
  }

  function hide(): void {
    gen++;
  }

  return { show, hide };
}
